import React, {ReactNode, useEffect, useState} from 'react'
import {
  IPOInfoFilter,
  IPOInfoFilterInitialState,
  IPOInfoModel,
  IPOTypes,
  IRemovedPOItem,
} from './core/_models'
import NormalMessage from '../common/NormalMessage'
import Loader from '../common/Loader'
import {useParams, useNavigate} from 'react-router-dom'
import {getPurchaseOrderInfo} from './core/_requests'
import SharedData from '../common/SharedData'
import ReturnTable from './components/return/ReturnTable'
import RemovedPurchaseTable from './components/purchase/RemovedPurchaseTable'
import { useIntl } from 'react-intl'

const ReturnOrder: React.FC = () => {
  const {formatMessage} = useIntl();
  const {orderID} = useParams()
  const navigate = useNavigate()

  const {currentUser,language} = SharedData();
  const [purchaseList, setPurchaseList] = useState<IPOInfoModel[]>([])
  const [removedList, setRemovedList] = useState<IRemovedPOItem[]>([])
  const [resultFilters, setResultFilters] = useState<IPOInfoFilter>({
    ...IPOInfoFilterInitialState,
    type: IPOTypes.return,
    supplier_order_id: orderID || ''
  })

  const [isLoading, setIsLoading] = useState(false)
  const [destination, setDestination] = useState<string>('')

  useEffect(() => {
    getData()
  }, [])

  const getData = async () => {
    setIsLoading(true)
    const {data} = await getPurchaseOrderInfo(resultFilters, currentUser?.token, language)
    setDestination(data?.destination ?? '')
    if (data) {
      const activeItems = Array.isArray(data.po_items)
        ? data.po_items
        : data.po_info
          ? Object.values(data.po_info)
          : []
      setPurchaseList(activeItems as IPOInfoModel[])

      const removedItems = Array.isArray(data.removed_po_items) ? data.removed_po_items : []
      setRemovedList(removedItems as IRemovedPOItem[])
    }
    setIsLoading(false)
  }

  const resultsData = (): ReactNode => {
    if (isLoading) {
      return <Loader />
    }
    if (purchaseList.length > 0) {
      return (
        <>
          <div className='card-body py-3'>
            <ReturnTable orders={purchaseList} />
          </div>
          {removedList.length > 0 && (
            <div className='card-body py-3 pt-0'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <h5 className='fw-bold mb-0'>{formatMessage({ id: "removed_po_products" , defaultMessage: "Removed PO Products" })}</h5>
                <span className='badge badge-light-primary'>{removedList.length}</span>
              </div>
              <RemovedPurchaseTable items={removedList} />
            </div>
          )}
        </>
      )
    }
    return <NormalMessage msg={formatMessage({ id: "no_data" })} />
  }

  return (
    <div className={`card`}>
      {/* begin::Header */}
      <div className='card-header border-0 pt-5 d-flex justify-content-between align-items-center'>
        <h3 className='card-title align-items-start flex-column mb-0'>
    <span className='card-label fw-bold fs-3'>
      {formatMessage({ id: "return_order_num" })}{orderID}
    </span>
    {/* WHICH STORE these goods leave. On a return this is the operative fact: the units come off
        that building's shelves and the ledger debit is scoped to it, so without it a supplier
        cannot tell which of their stocks this PO draws down. Hidden when the backend sends
        nothing, so a pre-Mart tenant sees the header exactly as before. */}
    {destination ? (
      <span className='text-muted fw-semibold fs-7 mt-1'>
        {formatMessage({ id: 'po_destination', defaultMessage: 'Destination' })}: {destination}
      </span>
    ) : null}
        </h3>

        <button
          className='btn btn-primary'
          onClick={() => navigate(`/orders/return-order/${orderID}/invoice`)}
        >
          {formatMessage({ id: "invoice", defaultMessage: "Invoice" })}
        </button>
      </div>
      {/* end::Header */}

      {resultsData()}
    </div>
  )
}

export {ReturnOrder}
